<template>
    <div class="qi-page">
        <div class="qi-page-header">
            <h4 class="mb-0 text-primary">Accounts Payable Aging</h4>
            <small class="text-muted">Money the business owes suppliers, grouped by how long it's been outstanding.</small>
        </div>

        <div v-if="summary" class="row mb-3">
            <div class="col-6 col-md-2-4 mb-3" style="flex:0 0 20%; max-width:20%;">
                <div class="stat-card">
                    <div class="stat-card__lbl">Current</div>
                    <div class="stat-card__val">{{ formatMoney(summary.current.amount) }}</div>
                    <div class="stat-card__sub">{{ summary.current.count }} bills</div>
                </div>
            </div>
            <div class="col-6 col-md-2-4 mb-3" style="flex:0 0 20%; max-width:20%;">
                <div class="stat-card">
                    <div class="stat-card__lbl">1-30 days</div>
                    <div class="stat-card__val">{{ formatMoney(summary.bucket_1_30.amount) }}</div>
                    <div class="stat-card__sub">{{ summary.bucket_1_30.count }} bills</div>
                </div>
            </div>
            <div class="col-6 col-md-2-4 mb-3" style="flex:0 0 20%; max-width:20%;">
                <div class="stat-card">
                    <div class="stat-card__lbl">31-60 days</div>
                    <div class="stat-card__val">{{ formatMoney(summary.bucket_31_60.amount) }}</div>
                    <div class="stat-card__sub">{{ summary.bucket_31_60.count }} bills</div>
                </div>
            </div>
            <div class="col-6 col-md-2-4 mb-3" style="flex:0 0 20%; max-width:20%;">
                <div class="stat-card">
                    <div class="stat-card__lbl">61-90 days</div>
                    <div class="stat-card__val">{{ formatMoney(summary.bucket_61_90.amount) }}</div>
                    <div class="stat-card__sub">{{ summary.bucket_61_90.count }} bills</div>
                </div>
            </div>
            <div class="col-6 col-md-2-4 mb-3" style="flex:0 0 20%; max-width:20%;">
                <div class="stat-card stat-card--danger">
                    <div class="stat-card__lbl">90+ days</div>
                    <div class="stat-card__val">{{ formatMoney(summary.bucket_90_plus.amount) }}</div>
                    <div class="stat-card__sub">{{ summary.bucket_90_plus.count }} bills</div>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-between align-items-center flex-wrap mb-2" style="gap:8px;">
            <div class="text-muted small" v-if="aging">{{ aging.rows.length }} open bill{{ aging.rows.length === 1 ? '' : 's' }} &middot; Total outstanding: <strong>{{ formatMoney(aging.grand_total) }}</strong></div>
            <button class="btn btn-outline-primary btn-sm" @click="exportCsv" :disabled="!aging || !aging.rows.length">
                <i class="bi bi-download mr-1"></i> Export CSV
            </button>
        </div>

        <div v-if="loading" class="text-center py-5"><span class="spinner-border"></span></div>
        <div v-else-if="!aging || !aging.rows.length" class="empty-state">
            <i class="bi bi-check-circle" style="font-size:2rem; opacity:.3;"></i>
            <p class="text-muted mt-2">No outstanding supplier balances.</p>
        </div>
        <div v-else class="table-responsive">
            <table class="table table-sm ar-table">
                <thead>
                    <tr>
                        <th>Supplier</th><th>Reference</th><th>Bill Date</th><th>Due Date</th>
                        <th class="text-right">Amount</th><th class="text-right">Paid</th>
                        <th class="text-right">Outstanding</th><th class="text-right">Days Overdue</th><th>Bucket</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in aging.rows" :key="row.purchase_id">
                        <td>{{ row.supplier_name }}</td>
                        <td class="font-weight-bold">{{ row.reference || '—' }}</td>
                        <td>{{ formatDate(row.bill_date) }}</td>
                        <td>{{ row.due_date ? formatDate(row.due_date) : '—' }}</td>
                        <td class="text-right">{{ formatMoney(row.total_amount) }}</td>
                        <td class="text-right">{{ formatMoney(row.paid_amount) }}</td>
                        <td class="text-right font-weight-bold">{{ formatMoney(row.balance) }}</td>
                        <td class="text-right" :class="row.days_overdue > 0 ? 'text-danger' : ''">{{ row.days_overdue }}</td>
                        <td><span class="badge" :class="bucketBadge(row.bucket)">{{ bucketLabel(row.bucket) }}</span></td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr class="font-weight-bold">
                        <td colspan="6" class="text-right">Total Outstanding</td>
                        <td class="text-right">{{ formatMoney(aging.grand_total) }}</td>
                        <td colspan="2"></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PayablesAging',
    data() {
        return {
            loading: true,
            summary: null,
            aging: null,
        };
    },
    computed: {
        currency() {
            const u = this.$store.getters.userDetails;
            return (u && u.company && u.company.companysettings && u.company.companysettings.currency) || 'KSh';
        },
    },
    created() {
        this.load();
    },
    methods: {
        formatMoney(v) {
            const n = Number(v || 0);
            return `${this.currency} ${n.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        formatDate(d) {
            return d ? new Date(d).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
        },
        bucketLabel(bucket) {
            return { current: 'Current', '1_30': '1-30 days', '31_60': '31-60 days', '61_90': '61-90 days', '90_plus': '90+ days' }[bucket] || bucket;
        },
        bucketBadge(bucket) {
            if (bucket === 'current') return 'badge-primary';
            if (bucket === '90_plus') return 'badge-danger';
            return 'badge-dark';
        },
        async load() {
            this.loading = true;
            try {
                const [summaryRes, agingRes] = await Promise.all([
                    axios.get('/api/reports/finance/payables/summary'),
                    axios.get('/api/reports/finance/payables/aging'),
                ]);
                this.summary = summaryRes.data;
                this.aging = agingRes.data;
            } catch (e) {
                this.$toasts.push({ type: 'error', message: 'Could not load the payables aging report.' });
            } finally {
                this.loading = false;
            }
        },
        exportCsv() {
            const header = ['Supplier', 'Reference', 'Bill Date', 'Due Date', 'Amount', 'Paid', 'Outstanding', 'Days Overdue', 'Bucket'];
            const rows = this.aging.rows.map(r => [
                r.supplier_name, r.reference || '', r.bill_date, r.due_date || '',
                r.total_amount, r.paid_amount, r.balance, r.days_overdue, this.bucketLabel(r.bucket),
            ]);
            const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `payables-aging-${new Date().toISOString().slice(0, 10)}.csv`;
            link.click();
        },
    },
};
</script>

<style scoped>
.small { margin: 0; }
.qi-page { padding: 16px; max-width: 1300px; margin: 0 auto; }
@media (max-width: 768px) { .qi-page { padding-bottom: 90px; } }
.qi-page-header { margin-bottom: 16px; }
.stat-card { border: 1px solid #eee; border-radius: 8px; padding: 14px; text-align: center; background: #fff; }
.stat-card--danger { border-color: #f5c6cb; }
.stat-card__lbl { font-size: 11px; text-transform: uppercase; letter-spacing: .5px; color: #6c757d; }
.stat-card__val { font-size: 20px; font-weight: 700; margin: 4px 0 2px; }
.stat-card__sub { font-size: 11px; color: #999; }
.ar-table th { font-size: .75rem; text-transform: uppercase; color: #6c757d; border-top: none; padding: 10px 12px; }
.ar-table td { padding: 12px; vertical-align: middle; }
.ar-table tfoot td { border-top: 2px solid #333; }
.empty-state { text-align: center; padding: 48px 16px; }
</style>
